import {Component, Inject, OnInit} from '@angular/core';
import {CharityService} from "../../../services/charity.service";
import {ICharityCategory} from "../../../interfaces/charity/ICharityCategory";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from "@angular/forms";
import {ICreateCharityCommand} from "../../../interfaces/charity/ICreateCharityCommand";
import {SnackbarService} from "../../../services/snackbar.service";
import {AddNewCategoryComponent} from "../add-new-category/add-new-category.component";
import {concatMap, iif, Observable, of, tap} from "rxjs";
import {INewCharity} from "../../../interfaces/charity/INewCharity";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MatError, MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatInput} from "@angular/material/input";
import {MatTooltip} from "@angular/material/tooltip";
import {MatIcon} from "@angular/material/icon";
import {DialogBaseComponent} from "../dialog-base/dialog-base.component";
import {CharityPayeeService} from "../../../services/charity-payee.service";
import {ICharityPayee} from "../../../interfaces/payee/ICharityPayee";
import {PermissionsService} from "../../../services/permissions.service";
import {PermissionTypes} from "../../../enum/PermissionTypes";

@Component({
  selector: 'app-add-new-charity',
  standalone: true,
  templateUrl: './add-new-charity.component.html',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatButton,
    MatInput,
    MatSuffix,
    MatIconButton,
    MatLabel,
    MatError,
    MatHint,
    MatTooltip,
    MatIcon,
    DialogBaseComponent
  ],
  styleUrls: ['./add-new-charity.component.scss']
})
export class AddNewCharityComponent implements OnInit {

  public charityCategories: ICharityCategory[] = [];

  /**
   * Active payees only. A retired payee is refused by the API, and offering it here would turn a validation
   * message into the way an admin finds out.
   */
  public activePayees: ICharityPayee[] = [];

  /**
   * Whether this admin can see payees at all. The list is served under ViewCharityPayouts, and a charity or game
   * admin without it is not entitled to know who an organization banks through — the same reason the assignment
   * endpoint exists separately from the charity list the player apps read.
   *
   * Checked before asking rather than handled after being refused: the request would 403, and a dialog that fires
   * a forbidden request every time it opens is one that will eventually be read as an intrusion attempt in a log
   * somebody is watching.
   */
  public canSeePayees: boolean = false;

  /**
   * Whether the payee list failed to load, kept apart from an empty list on purpose. Both mean the charities are paid
   * directly, but only one of them means this game has no central payee, and telling an admin that when the request
   * merely failed is how six charities get paid directly by a decision nobody made.
   */
  public payeesFailedToLoad: boolean = false;
  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public showErrorMessage: boolean = false;
  public uiStateEnumForTemplate = UIStateEnum;
  public createCharityForm = new FormGroup({
    categoryControl: new FormControl<ICharityCategory | null>(null, Validators.required),
    charityNameControl: new FormControl<string | null>('', [this.notAddedToRequestValidator.bind(this)]),
    charityEnabled: new FormControl<boolean>(true),
    // Empty string rather than null: mat-select needs a value to show "Paid directly" as the chosen option
    // rather than as an empty field, and paying a charity directly is a decision, not an absence of one.
    charityPayeeIdControl: new FormControl<string>('', {nonNullable: true})
  });
  public listOfNewCharities: INewCharity[] = [];

  constructor(private charityService: CharityService,
              private charityPayeeService: CharityPayeeService,
              private permissionsService: PermissionsService,
              private errorHandlingService: ErrorHandlingService,
              private matDialogRef: MatDialogRef<AddNewCharityComponent>,
              private matDialog: MatDialog,
              @Inject(MAT_DIALOG_DATA) public gameId: string,
              private snackBarService: SnackbarService) {
  }

  ngOnInit(): void {
    this.canSeePayees = this.permissionsService.userHasPermission(PermissionTypes.ViewCharityPayouts);

    if (this.canSeePayees)
    {
      // Failing to load payees must not stop a charity being added: the assignment can be made afterwards from the
      // Charity Payees screen.
      this.charityPayeeService.fetchByGameId(this.gameId).subscribe({
        next: (payeesP) => {
          this.activePayees = payeesP.filter(payeeP => payeeP.Active);
          this.payeesFailedToLoad = false;
        },
        error: () => {
          this.activePayees = [];
          this.payeesFailedToLoad = true;
        }
      });
    }

    this.charityService.charityCategoriesByGame(this.gameId).subscribe({
      next: (categories) => {
        this.charityCategories = categories;
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayDialogLevelErrorMessage(err);
      }
    });
  }


  notAddedToRequestValidator(): ValidationErrors | null {
    if (this.showErrorMessage) {
      return {notAddedToRequest: true};
    }

    return null;
  }

  addToCharityList() {
    this.showErrorMessage = false;
    const charityNameControl = this.createCharityForm.controls.charityNameControl;
    const chosenCategory = this.createCharityForm.controls.categoryControl;
    if (chosenCategory.value) {
      let newSortOrder = (this.charityCategories.length - 1) + this.listOfNewCharities.length
      const newCharity: INewCharity = {
        active: true,
        name: charityNameControl.value!,
        sortOrder: newSortOrder
      };

      this.listOfNewCharities.push(newCharity);

      charityNameControl.setValue('');
    }

  }

  removeFromCharityList(itemP: INewCharity) {
    this.listOfNewCharities = this.listOfNewCharities.filter((charity) => charity.name !== itemP.name);
  }

  openAddNewCategoryDialog() {
    const dialog = this.matDialog.open(AddNewCategoryComponent, {
      width: '650px',
      data: this.gameId
    });

    dialog.componentInstance.charityCategories = this.charityCategories;

    dialog.afterClosed()
      .pipe(concatMap((res) =>
        iif(() => res, this.getCharitiesUpdateCategory(res), of(false))))
      .subscribe({
        error: (err) => {
          this.uiState = UIStateEnum.ShowData;
          this.errorHandlingService.displayDialogLevelErrorMessage(err);
        }
      });
  }

  private getCharitiesUpdateCategory(newCategoryIdP: string): Observable<any> {
    return this.charityService.charityCategoriesByGame(this.gameId).pipe(tap((categories) => {
      this.charityCategories = categories;

      const newlyCreatedCat = this.charityCategories.find((cat) => cat.Id === newCategoryIdP);
      if (newlyCreatedCat) {
        this.createCharityForm.get('categoryControl')?.setValue(newlyCreatedCat)
      }
      this.uiState = UIStateEnum.ShowData;
    }));
  }

  submitNewCharities() {
    this.showErrorMessage = false;
    if (this.listOfNewCharities.length <= 0) {
      this.showErrorMessage = true;
      this.createCharityForm.controls.charityNameControl.updateValueAndValidity();
      return;
    }

    if (this.createCharityForm.valid) {

      this.uiState = UIStateEnum.ShowLoading;
      const chosen_payee_id = this.createCharityForm.controls.charityPayeeIdControl.value;

      let newCharityCommand: ICreateCharityCommand = {
        charityCategoryId: this.createCharityForm.get('categoryControl')!.value!.Id,
        gameId: this.gameId,
        newCharities: this.listOfNewCharities,
        charityPayeeId: chosen_payee_id === '' ? null : chosen_payee_id
      };

      this.charityService.createNewCharity(newCharityCommand).subscribe({
        next: (res) => {
          this.uiState = UIStateEnum.ShowData;
          this.matDialogRef.close(res.IsSuccess);
          this.snackBarService.openSuccessfulSnackBar('Successfully Add New Charity');
        },
        error: (err) => {
          this.uiState = UIStateEnum.ShowData;
          this.errorHandlingService.displayDialogLevelErrorMessage(err,  true, 'An error occurred while attempting to add your charity(ies). Please try again or contact your system administrator for assistance.');
        }
      })
    }
  }
}
